import i18n from '@/i18n';
import { ApiError } from './api';

/**
 * Normalização e tradução de erros de API.
 *
 * Existe porque o backend responde em quatro formatos incompatíveis — `{error, message}`,
 * só `{error}`, `{error, message, atual, limite}` nos 402, e **corpo vazio** nos
 * `Results.Forbid()` e `Results.NotFound()`, que juntos são a maioria. Nesse último caso
 * não há código nenhum para traduzir, e sem um fallback por status o usuário lê
 * "Forbidden" cru. Daí o mapa STATUS_FALLBACK.
 *
 * Usa o singleton do i18next em vez de um hook para poder ser chamado do queryClient,
 * que é módulo e não componente.
 */

/** Erro de rede: fetch rejeita com TypeError quando não há resposta nenhuma. */
const NETWORK = 'NETWORK';

const STATUS_FALLBACK: Record<number, string> = {
  400: 'BAD_REQUEST',
  401: 'SESSION_EXPIRED',
  402: 'QUOTA_EXCEEDED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  413: 'PAYLOAD_TOO_LARGE',
  429: 'RATE_LIMITED',
};

export type NormalizedError = {
  code: string;
  status?: number;
  serverMessage?: string;
};

export function statusFallbackCode(status: number): string {
  return STATUS_FALLBACK[status] ?? (status >= 500 ? 'SERVER_ERROR' : 'generic');
}

/** Todos os códigos que precisam existir no dicionário — usado pelo teste de paridade. */
export const FALLBACK_CODES = [
  ...Object.values(STATUS_FALLBACK),
  'SERVER_ERROR',
  NETWORK,
  'generic',
];

export function normalizeError(err: unknown): NormalizedError {
  if (err instanceof ApiError) {
    return {
      code: err.code ?? statusFallbackCode(err.status),
      status: err.status,
      serverMessage: err.serverMessage,
    };
  }
  // fetch rejeita com TypeError quando a API está fora do ar, o DNS falha ou o CORS barra.
  if (err instanceof TypeError) return { code: NETWORK };
  return { code: 'generic' };
}

/**
 * Precedência: dicionário i18n → mensagem do servidor → genérico.
 * A mensagem do servidor só aparece quando o código é desconhecido pelo front — assim um
 * código novo no backend degrada para algo legível em vez de "Ocorreu um erro".
 */
export function translateError(err: unknown): string {
  const { code, serverMessage } = normalizeError(err);

  // `exists` e não `t(..., { defaultValue: '' })`: o i18n do projeto roda com
  // `returnEmptyString: false`, que trata string vazia como "não encontrado" e devolve
  // a própria CHAVE. O truque do defaultValue faria "errors.CODIGO_X" aparecer na tela.
  if (i18n.exists(`errors.${code}`, { ns: 'common' })) {
    return i18n.t(`errors.${code}`, { ns: 'common' });
  }
  if (serverMessage) return serverMessage;
  return i18n.t('errors.generic', { ns: 'common' });
}
